/**
 * useTaskEstimate — the React binding for saving/loading a task's compact
 * engineering estimate, and for asking Vector (AI) to suggest one.
 *
 * Strict layering: components call THIS hook; the hook calls
 * {@link TaskEstimateStore} (the estimate) + {@link TaskStore} (mirroring the
 * headline numbers back onto the task) + {@link suggestEstimate} (AI, through
 * the inference facade). Components never import a store or the api directly.
 *
 * This hook is intentionally context-free (it takes the task as an argument) so
 * the SAME persistence path serves both places an estimate can be created: the
 * "Estimate now" step right after quick-create, and the task page's
 * [Estimate] / [Edit Estimate] action.
 */

import { useCallback, useMemo } from 'react';
import { useApi } from '../../../hooks/useApi';
import { TaskStore } from '../../tasks/taskStore';
import type { Task } from '../../tasks/types';
import type { EstimateInputs, EstimateResult } from '../engine/estimation';
import { TaskEstimateStore } from './taskEstimateStore';
import { effectiveEstimate } from './taskEstimateModel';
import { suggestEstimate, type EstimateContext } from './aiEstimate';
import type {
  AiEstimateSuggestion,
  EstimateOverride,
  EstimateSource,
  TaskEstimate,
  TaskEstimateDraft,
} from './types';
import type { KbSource } from '../../../api';

export interface SaveEstimateResult {
  estimate: TaskEstimate;
  task: Task;
}

export interface UseTaskEstimateResult {
  /** Loads a saved estimate document (full inputs + breakdown) by id. */
  load: (estimateId: string) => Promise<TaskEstimate | undefined>;
  /**
   * Persists an estimate for a task and mirrors effortDays / storyPoints /
   * confidence / estimateId back onto the task. When `existingEstimateId` is
   * supplied the estimate is updated in place; otherwise a new one is created.
   * Returns both the saved estimate and the updated task.
   */
  save: (args: {
    task: Task;
    inputs: EstimateInputs;
    result: EstimateResult;
    source: EstimateSource;
    rationale?: string[];
    /** KB citations (source==='ai' grounded); persisted for provenance. */
    sources?: KbSource[];
    /** Manual override; when present its numbers become the effective estimate. */
    override?: EstimateOverride;
    existingEstimateId?: string;
  }) => Promise<SaveEstimateResult>;
  /** Asks Vector (AI) to suggest engine inputs for a task context. */
  suggest: (ctx: EstimateContext) => Promise<AiEstimateSuggestion>;
}

export function useTaskEstimate(): UseTaskEstimateResult {
  const api = useApi();
  const stores = useMemo(
    () => ({ estimates: new TaskEstimateStore(api), tasks: new TaskStore(api) }),
    [api],
  );

  const load = useCallback(
    (estimateId: string) => stores.estimates.get(estimateId),
    [stores],
  );

  const save = useCallback(
    async ({
      task,
      inputs,
      result,
      source,
      rationale,
      sources,
      override,
      existingEstimateId,
    }: {
      task: Task;
      inputs: EstimateInputs;
      result: EstimateResult;
      source: EstimateSource;
      rationale?: string[];
      sources?: KbSource[];
      override?: EstimateOverride;
      existingEstimateId?: string;
    }): Promise<SaveEstimateResult> => {
      const draft: TaskEstimateDraft = {
        taskId: task.id,
        changeType: inputs.changeType,
        complexity: inputs.complexity,
        extraWork: inputs.extraWork,
        unknowns: inputs.unknowns,
        customChangeType: inputs.customChangeType,
        customBaseDays: inputs.customBaseDays,
        breakdown: result.breakdown,
        effortDays: result.engineeringEffortDays,
        storyPoints: result.storyPoints,
        confidence: result.confidence,
        isSpike: result.isSpike,
        timeboxDays: result.timeboxDays,
        source,
        rationale,
        sources,
        override,
      };

      // Write (or update) the estimate document.
      let estimate: TaskEstimate | undefined;
      if (existingEstimateId) {
        const existing = await stores.estimates.get(existingEstimateId);
        if (existing) {
          estimate = await stores.estimates.applyDraft(existing, draft);
        }
      }
      if (!estimate) {
        estimate = await stores.estimates.create(task.roomId, draft);
      }

      // The EFFECTIVE numbers (override when present, else computed) are what
      // gets mirrored onto the task for capacity / rollups.
      const effective = effectiveEstimate(
        {
          effortDays: result.engineeringEffortDays,
          storyPoints: result.storyPoints,
          confidence: result.confidence,
        },
        override,
      );

      // Mirror the effective numbers + the link back onto the task.
      const updatedTask = await stores.tasks.update({
        ...task,
        estimateId: estimate.id,
        effortDays: effective.effortDays,
        storyPoints: effective.storyPoints,
        confidence: effective.confidence,
        updatedAt: new Date().toISOString(),
      });

      return { estimate, task: updatedTask };
    },
    [stores],
  );

  const suggest = useCallback(
    (ctx: EstimateContext) =>
      suggestEstimate(
        { inference: api.inference, knowledgeBase: api.knowledgeBase },
        ctx,
      ),
    [api],
  );

  return { load, save, suggest };
}
